/*
  © 2024 Jhango
  https://www.jhango.com
*/

class ProductForm extends HTMLElement {
  constructor () {
    super()

    this.form = this.querySelector('form')
    this.productOptions = this.querySelectorAll('.product-options [name*="option"]')
    this.atcBtn = this.querySelector('.btn-atc')
    this.buyBtn = this.querySelector('.btn-buy')

    this.form.addEventListener('submit', event => this.onSubmit(event))

    this.productOptions.forEach(elem =>
      elem.addEventListener('change', event => this.onChangeProductOption(event)))

    this.buyBtn?.addEventListener('click', event => this.onClickBuyBtn(event))

    this.querySelectorAll('select[name="id"').forEach(select => {
      select.addEventListener('change', () => this.onChangeVariant(select))
    })

    this.handleOutOfStockOptions()
  }

  async onChangeProductOption (event) {
    event.preventDefault()

    const selectedOptionsArray = []

    this.productOptions.forEach(elem => {
      if ((elem.type === 'radio' && elem.checked) || elem.type === 'select-one') {
        const position = parseInt(elem.getAttribute('data-option-position'), 10);
        if (position && !isNaN(position)) {
          selectedOptionsArray[position - 1] = elem.value;
        } else {
          selectedOptionsArray.push(elem.value);
        }
      }
    })

    // Filter out undefined if there are gaps (shouldn't be)
    const selectedOptions = selectedOptionsArray.filter(val => val !== undefined);

    // console.log(selectedOptions)

    const selectedVariant = JSON.parse(this.form.dataset.variants).find(variant =>
      JSON.stringify(variant.options) === JSON.stringify(selectedOptions)
    )

    console.log(selectedVariant)

    this.adjustPricing(selectedVariant)
    this.adjustButtons(selectedVariant)
    this.handleOutOfStockOptions()

    if (selectedVariant) {
      this.querySelector('[name="id"]').value = selectedVariant.id

      if (this.closest('.product-main')) {
        const url = new URL(window.location)
        url.searchParams.set('variant', selectedVariant.id)
        window.history.replaceState({}, '', url)
      }

      window.dispatchEvent(new CustomEvent('kt.product.variant.change', {
        detail: selectedVariant
      }))

      this.fetchNewDocument(selectedVariant)
    }
  }

  adjustPricing (selectedVariant) {
    if (!selectedVariant) return

    const comparePrice = this.querySelector('.product-price-compare')
    const finalPrice = this.querySelector('.product-price-final')
    const compareBadge = this.querySelector('.product-sale-badge')
    const soldOutBadge = this.querySelector('.product-sold-out-badge')

    if (selectedVariant.compare_at_price > selectedVariant.price) {
      if (comparePrice) comparePrice.textContent = window.Shopify.formatMoney(selectedVariant.compare_at_price)
      comparePrice?.removeAttribute('hidden')
      if (finalPrice) {
          finalPrice.innerHTML = `<span class="visually-hidden">${window.theme.locales.product.sale_price}}</span>${window.Shopify.formatMoney(selectedVariant.price)}`
          finalPrice.classList.add('product-price-final-sale')
      }
      compareBadge?.removeAttribute('hidden')
      if (compareBadge && compareBadge.dataset.discountType === 'percentage') {
        const savings = 100 - (Math.round(selectedVariant.price / selectedVariant.compare_at_price * 100)) + '%'
        compareBadge.textContent = `${window.theme.locales.product.save} ${savings}`
      } else if (compareBadge) {
        const savings = window.Shopify.formatMoney(selectedVariant.compare_at_price - selectedVariant.price)
        compareBadge.textContent = `${window.theme.locales.product.save} ${savings}`
      }
    } else {
      if (comparePrice) comparePrice.textContent = ''
      comparePrice?.setAttribute('hidden', 'hidden')
      if (finalPrice) {
          finalPrice.innerHTML = window.Shopify.formatMoney(selectedVariant.price)
          finalPrice.classList.remove('product-price-final-sale')
      }
      if (compareBadge) compareBadge.textContent = ''
      compareBadge?.setAttribute('hidden', 'hidden')
    }

    if (selectedVariant.available) {
      soldOutBadge?.setAttribute('hidden', 'hidden')
    } else {
      soldOutBadge?.removeAttribute('hidden')
    }
  }

  adjustButtons (selectedVariant) {
    if (selectedVariant) {
      if (selectedVariant.available) {
        this.atcBtn?.removeAttribute('disabled')
        this.buyBtn?.removeAttribute('disabled')
        if (this.atcBtn) this.atcBtn.innerHTML = window.theme.locales.product.add_to_cart
      } else {
        this.atcBtn?.setAttribute('disabled', 'disabled')
        this.buyBtn?.setAttribute('disabled', 'disabled')
        if (this.atcBtn) this.atcBtn.innerHTML = window.theme.locales.product.sold_out
      }
    } else {
      this.atcBtn?.setAttribute('disabled', 'disabled')
      this.buyBtn?.setAttribute('disabled', 'disabled')
      if (this.atcBtn) this.atcBtn.innerHTML = window.theme.locales.product.unavailable
    }
  }

  async fetchNewDocument (selectedVariant) {
    const respoonse = await fetch(`${this.form.dataset.productUrl}?variant=${selectedVariant.id}`)
    const text = await respoonse.text()
    const newDocument = new DOMParser().parseFromString(text, 'text/html')

    window.dispatchEvent(new CustomEvent('kt.product.variant.change.newDoc', {
      detail: newDocument
    }))
  }

  async onSubmit (event) {
    event.preventDefault()

    this.atcBtn.classList.add('loading')
    this.atcBtn.disabled = true
    this.atcBtn.setAttribute('aria-busy', 'true')

    const formData = new FormData(this.form)

    const outsideId = this.querySelector('[name="id"]')?.value;
    if (outsideId) formData.set('id', outsideId);
    
    const outsideQty = this.querySelector('input[name="quantity"]')?.value;
    if (outsideQty) formData.set('quantity', outsideQty);

    await document.querySelector('cart-container').add(formData)

    this.atcBtn.classList.remove('loading')
    this.atcBtn.disabled = false
    this.atcBtn.setAttribute('aria-busy', 'false')
  }

  onClickBuyBtn () {
    this.buyBtn.classList.add('loading')
    const variantId = this.querySelector('[name="id"]').value
    const qty = Number(this.querySelector('input[name="quantity"]')?.value || 1)
    window.location.href = `/cart/${variantId}:${qty}`
  }

  onChangeVariant (select) {
    const variantImage = select[select.selectedIndex].dataset.variantImage

    if (variantImage.length) {
      select.closest('.product-card').querySelector('.product-card-img-wrapper img').setAttribute('src', variantImage)
    }
  }

  handleOutOfStockOptions () {
    if (!this.form.dataset.variants) return

    const variants = JSON.parse(this.form.dataset.variants)

    const selectedOption1 = this.querySelector('input[data-option-position="1"]:checked')?.value
    const selectedOption2 = this.querySelector('input[data-option-position="2"]:checked')?.value
    const selectedOption3 = this.querySelector('input[data-option-position="3"]:checked')?.value

    this.querySelectorAll('input[data-option-position="1"]').forEach(input => {
      const correspondingVariant = variants.find(variant => {
        if (selectedOption3) {
          if (variant.option1 === input.value && variant.option2 === selectedOption2 && variant.option3 === selectedOption3) return variant
        } else if (selectedOption2) {
          if (variant.option1 === input.value && variant.option2 === selectedOption2) return variant
        } else {
          if (variant.option1 === input.value) return variant
        }
        return null
      })

      if (!correspondingVariant || correspondingVariant.available) {
        input.classList.remove('disabled')
      } else {
        input.classList.add('disabled')
      }
    })

    this.querySelectorAll('input[data-option-position="2"]').forEach(input => {
      const correspondingVariant = variants.find(variant => {
        if (selectedOption3) {
          if (variant.option2 === input.value && variant.option1 === selectedOption1 && variant.option3 === selectedOption3) return variant
        } else {
          if (variant.option2 === input.value && variant.option1 === selectedOption1) return variant
        }
        return null
      })

      if (!correspondingVariant || correspondingVariant.available) {
        input.classList.remove('disabled')
      } else {
        input.classList.add('disabled')
      }
    })

    this.querySelectorAll('input[data-option-position="3"]').forEach(input => {
      const correspondingVariant = variants.find(variant => {
        if (variant.option3 === input.value && variant.option1 === selectedOption1 && variant.option2 === selectedOption2) return variant
        return null
      })

      if (!correspondingVariant || correspondingVariant.available) {
        input.classList.remove('disabled')
      } else {
        input.classList.add('disabled')
      }
    })
  }
}
customElements.define('product-form', ProductForm)

class ProductMediaGallery extends HTMLElement {
  constructor () {
    super()

    const initialSlide = Number(this.dataset.initialSlide)

    const thumbsEl = this.querySelector('.swiper-thumbs');
    if (thumbsEl) {
      this.thumbs = new window.Swiper(thumbsEl, this.thumbsOptions())
    }

    const mainEl = this.querySelector('.swiper-main');
    if (mainEl) {
      this.main = new window.Swiper(mainEl, this.mainOptions())
      this.main.slideTo(initialSlide)
    }

    this.setStickyPosition()
    this.listenVariantChange()
    this.initFullScreenGallery()
  }

  thumbsOptions () {
    /** @type {SwiperOptions} */
    const options = {
      slidesPerView: Number(this.dataset.thumbsPerView) || 5,
      spaceBetween: 10,
      mousewheel: true,
      navigation: {
        nextEl: this.querySelector('.swiper-thumbs-next'),
        prevEl: this.querySelector('.swiper-thumbs-prev')
      }
    }
    return options
  }

  mainOptions () {
    /** @type {SwiperOptions} */
    const options = {
      effect: this.dataset.effect,
      speed: Number(this.dataset.speed),
      rewind: true,
      pagination: {
        type: this.dataset.paginationType,
        el: '.swiper-pagination',
        dynamicBullets: true,
        dynamicMainBullets: 2,
        renderFraction: function (currentClass, totalClass) {
          return `<span class="${currentClass}"></span>/<span class="${totalClass}"></span>`
        }
      },
      navigation: {
        nextEl: this.querySelector('.swiper-button-next'),
        prevEl: this.querySelector('.swiper-button-prev')
      },
      thumbs: {
        swiper: this.thumbs,
        autoScrollOffset: 1
      },
      cubeEffect: {
        shadow: false
      },
      creativeEffect: {
        prev: {
          shadow: true,
          origin: 'left center',
          translate: ['-5%', 0, -200],
          rotate: [0, 100, 0]
        },
        next: {
          origin: 'right center',
          translate: ['5%', 0, -200],
          rotate: [0, -100, 0]
        }
      },
      scrollbar: {
        enabled: this.dataset.scrollbar === 'true',
        el: this.querySelector('.swiper-scrollbar'),
        draggable: true,
        hide: true
      },
      breakpoints: {
        0: { slidesPerView: 1 * (this.dataset.partialSlidesMobile === 'true' ? 1.2 : 1) },
        600: { slidesPerView: 1 }
      },
      on: {
        slideChange: this.onSlideChange.bind(this)
      }
    }
    return options
  }

  onSlideChange () {
    this.querySelectorAll('video').forEach(video => video.pause())

    setTimeout(() => {
      if (this.querySelector('.swiper-main .swiper-slide-active video')) {
        this.querySelector('.swiper-main-wrapper').setAttribute('data-active-slide-is-video', 'true')
      } else {
        this.querySelector('.swiper-main-wrapper').setAttribute('data-active-slide-is-video', 'false')
      }
    }, 500)
  }

  setStickyPosition () {
    if (this.closest('.product-main') && window.matchMedia('(min-width: 1200px)').matches) {
      const headerGroup = document.querySelector('#header-group.sticky-top')
      const pdpExtraContent = document.querySelector('#pdp-extra-content')

      let oldScroll = window.scrollY

      window.addEventListener('scroll', () => {
        const newScroll = window.scrollY
        if (newScroll > oldScroll) {
          if (newScroll > headerGroup.clientHeight) {
            this.style.top = '20px'
            if (pdpExtraContent) {
              document.querySelector('#pdp-extra-content').style.top = '20px'
            }
          }
        } else if (newScroll < oldScroll) {
          this.style.top = `${headerGroup.clientHeight + 20}px`
          if (pdpExtraContent) {
            document.querySelector('#pdp-extra-content').style.top = `${headerGroup.clientHeight + 20}px`
          }
        }

        oldScroll = Math.max(window.scrollY, 0)
      })
    }
  }

  listenVariantChange () {
    window.addEventListener('kt.product.variant.change', (event) => {
      const selectedVariant = event.detail

      let keyword = null;
      if (selectedVariant.options) {
        const colorValue = selectedVariant.options.find(opt => {
          if (typeof opt !== 'string') return false;
          const val = opt.toLowerCase();
          return val.includes('yellow') || val.includes('rose') || val.includes('white') || val.includes('rhodium');
        });
        if (colorValue) {
          const val = colorValue.toLowerCase();
          if (val.includes('yellow')) keyword = 'yellow';
          else if (val.includes('rose')) keyword = 'rose';
          else if (val.includes('white') || val.includes('rhodium')) keyword = 'white';
        }
      }

      if (keyword) {
        const slides = Array.from(this.querySelectorAll('.swiper-main .swiper-slide:not(.swiper-slide-duplicate) img'));
        let targetImg = slides.find(img => {
          const src = (img.src || '').toLowerCase();
          const alt = (img.alt || '').toLowerCase();
          if (keyword === 'yellow') return src.includes('yellow') || alt.includes('yellow') || src.includes('yellow_gold') || src.includes('-yg') || src.includes('_yg');
          if (keyword === 'rose') return src.includes('rose') || alt.includes('rose') || src.includes('rose_gold') || src.includes('-rg') || src.includes('_rg');
          if (keyword === 'white') return src.includes('white') || src.includes('rhodium') || alt.includes('white') || alt.includes('rhodium') || src.includes('-wg') || src.includes('_wg');
          return false;
        });
        
        // Fallback for Yellow Gold if no specific image is found (assume hero image)
        if (!targetImg && keyword === 'yellow' && slides.length > 0) {
          targetImg = slides[0];
        }
        if (targetImg) {
          const slide = targetImg.closest('.swiper-slide');
          if (slide && slide.parentNode && this.main && typeof this.main.slideTo === 'function') {
            const allRealSlides = Array.from(slide.parentNode.querySelectorAll('.swiper-slide:not(.swiper-slide-duplicate)'));
            const slideIndex = allRealSlides.indexOf(slide);
            if (slideIndex !== -1) {
              this.main.slideTo(slideIndex);
              // BUG 3 FIX: Also update thumbs swiper to match
              if (this.thumbs && typeof this.thumbs.slideTo === 'function') {
                this.thumbs.slideTo(slideIndex);
              }
            }
          }
          return;
        }
      }

      if (selectedVariant && selectedVariant.featured_media && this.main && typeof this.main.slideTo === 'function') {
        const pos = selectedVariant.featured_media.position - 1;
        this.main.slideTo(pos);
        // BUG 3 FIX: Keep thumbs in sync too
        if (this.thumbs && typeof this.thumbs.slideTo === 'function') {
          this.thumbs.slideTo(pos);
        }
      }
    }, false)
  }

  async initFullScreenGallery () {
    if (this.dataset.fullScreen === 'false') return

    const vendorScript = document.querySelector('script[src*="vendor-glightbox.min"]')

    vendorScript.onload = () => {
      window.GLightbox({
        selector: `[data-gallery="gallery-${this.dataset.productId}"]`,
        loop: true,
        openEffect: 'zoom',
        closeEffect: 'fade',
        slideEffect: this.dataset.fullScreenEffect
      })
    }
  }
}
customElements.define('product-media-gallery', ProductMediaGallery)

class QtySwitcher extends HTMLElement {
  constructor () {
    super()

    this.qtyInput = this.querySelector('input')
    this.minusBtn = this.querySelector('button.btn-qty-minus')
    this.plusBtn = this.querySelector('button.btn-qty-plus')

    this.qtyInput.addEventListener('change', () => this.onQtyInputChange())
    this.minusBtn.addEventListener('click', () => this.onMinusBtnClick())
    this.plusBtn.addEventListener('click', () => this.onPlusBtnClick())

    window.addEventListener('kt.product.variant.change.newDoc', (event) => this.onVariantChange(event))
  }

  onQtyInputChange () {
    this.validateButtons()
  }

  onMinusBtnClick () {
    this.qtyInput.value = Number(this.qtyInput.value) - 1
    this.validateButtons()
    this.qtyInput.dispatchEvent(new CustomEvent('change'))
  }

  onPlusBtnClick () {
    this.qtyInput.value = Number(this.qtyInput.value) + 1
    this.validateButtons()
    this.qtyInput.dispatchEvent(new CustomEvent('change'))
  }

  validateButtons () {
    if (this.qtyInput.getAttribute('min') && Number(this.qtyInput.value) <= Number(this.qtyInput.getAttribute('min'))) {
      this.minusBtn.disabled = true
      this.qtyInput.value = this.qtyInput.getAttribute('min')
    } else {
      this.minusBtn.disabled = false
    }
    if (this.qtyInput.getAttribute('max') && Number(this.qtyInput.value) >= Number(this.qtyInput.getAttribute('max'))) {
      this.plusBtn.disabled = true
      this.qtyInput.value = this.qtyInput.getAttribute('max')
    } else {
      this.plusBtn.disabled = false
    }
  }

  onVariantChange (event) {
    if (!this.closest('.product-block-atc')) return

    const newDoc = event.detail
    const newQtyInput = newDoc.querySelector('.product-block-atc qty-switcher input')

    this.qtyInput.setAttribute('max', newQtyInput.getAttribute('max'))
    this.validateButtons()
  }
}
customElements.define('qty-switcher', QtySwitcher)

class ProductStockIndicator extends HTMLElement {
  constructor () {
    super()

    this.progressBar = this.querySelector('.progress-bar')
    this.animateProgressBar()
    this.handleVariantChange()
  }

  animateProgressBar () {
    setTimeout(() => {
      this.progressBar.style.width = this.progressBar.dataset.width
    }, 250)
  }

  handleVariantChange () {
    window.addEventListener('kt.product.variant.change', async () => {
      this.querySelector('.product-block-stock-indicator-inner').style.opacity = '.25'
    })

    window.addEventListener('kt.product.variant.change.newDoc', async (e) => {
      const newDoc = e.detail

      this.replaceWith(newDoc.querySelector('[id*="product-block-stock-indicator"]'))
      this.animateProgressBar()
    })
  }
}
customElements.define('product-stock-indicator', ProductStockIndicator)

class ProductQuantityBreaks extends HTMLElement {
  constructor () {
    super()

    this.handleInputChange()
    this.handleVariantChange()
  }

  handleInputChange (btn) {
    this.querySelectorAll('input').forEach(input => {
      input.addEventListener('change', () => {
        const qtyField = input.closest('form').querySelector('[name="quantity"]')
        qtyField.value = input.value
        qtyField.dispatchEvent(new CustomEvent('change'))

        const atcBtn = this.closest('form').querySelector('.btn-atc')

        setTimeout(() => {
          atcBtn.classList.add('animate__animated', 'animate__shakeX')
        }, 250)

        setTimeout(() => {
          atcBtn.classList.remove('animate__animated', 'animate__shakeX')
        }, 1500)
      })
    })
  }

  handleVariantChange () {
    window.addEventListener('kt.product.variant.change', async () => {
      this.querySelector('.product-block-quantity-breaks-list').style.opacity = '.25'
    })

    window.addEventListener('kt.product.variant.change.newDoc', async (e) => {
      const newDoc = e.detail

      this.replaceWith(newDoc.querySelector('product-quantity-breaks'))
      this.handleInputChange()
    })
  }
}
customElements.define('product-quantity-breaks', ProductQuantityBreaks)

class ProductQuickButton extends HTMLElement {
  constructor () {
    super()

    this.btn = this.querySelector('.btn-quick-view')
    this.modalCustomElem = document.querySelector('product-quick-view-modal')

    this.btn.addEventListener('click', async (event) => this.fetchProductData(event))
  }

  async fetchProductData (event) {
    event.preventDefault()

    this.setLoading(true)

    const response = await fetch(this.btn.dataset.productUrl)

    if (response.ok) {
      const text = await response.text()
      const newDoc = new DOMParser().parseFromString(text, 'text/html')

      document.querySelector('product-quick-view-modal').showModal(newDoc)
    } else {
      // TODO: Handle Errors
    }

    this.setLoading(false)
  }

  setLoading (isLoading) {
    if (isLoading) {
      this.btn.classList.add('loading')
      this.btn.disabled = true
      this.btn.setAttribute('aria-busy', 'true')
    } else {
      this.btn.classList.remove('loading')
      this.btn.disabled = false
      this.btn.removeAttribute('aria-busy')
    }
  }
}
customElements.define('product-quick-view-button', ProductQuickButton)

class ProductQuickViewModal extends HTMLElement {
  constructor () {
    super()

    this.modalElem = document.querySelector('#quick-view-modal')

    this.modalElem.addEventListener('hidden.bs.modal', () => {
      this.modalElem.querySelector('.product').innerHTML = '<div data-quick-view-contents></div>'
    })

    window.addEventListener('kt.cart.added', () => {
      window.bootstrap.Modal.getOrCreateInstance(this.modalElem).hide()
    })
  }

  async showModal (newDoc) {
    this.modalElem.querySelector('[data-quick-view-contents]').replaceWith(
      newDoc.querySelector('.product-main [data-quick-view-contents]'))

    this.modalElem.querySelector('.product-title').setAttribute('id', 'quick-view-modal-label')

    this.modalElem.querySelectorAll('.collapse-wrapper').forEach(collapseWrapper => {
      collapseWrapper.querySelector('[data-bs-toggle="collapse"]').setAttribute('aria-expanded', 'false')
      collapseWrapper.querySelector('.collapse').classList.remove('show')
    })

    this.modalElem.querySelectorAll('.product-options [name*="option"]').forEach(elem => {
      elem.setAttribute('name', `${elem.name}-qv`)
      elem.setAttribute('id', `${elem.id}-qv`)
    })

    this.modalElem.querySelectorAll('.product-options label').forEach(label => {
      label.setAttribute('for', `${label.getAttribute('for')}-qv`)
    })

    this.modalElem.querySelectorAll('.collapse').forEach(elem => {
      elem.addEventListener('shown.bs.collapse', () => {
        elem.closest('.product-block-collapse')?.scrollIntoView({ behavior: 'smooth' })
      })
    })

    window.bootstrap.Modal.getOrCreateInstance(this.modalElem).show()
    window.dispatchEvent(new CustomEvent('kt.product.quick_view.modal_shown'))
  }
}
customElements.define('product-quick-view-modal', ProductQuickViewModal)

class ProductDropupSelector extends HTMLElement {
  constructor () {
    super()

    this.productCard = this.closest('.product-card')
    this.dropdownBtn = this.querySelector('.dropdown-toggle')

    this.querySelector('.dropdown-menu').style.maxHeight =
      this.productCard.offsetHeight - this.dropdownBtn.offsetHeight - 2 + 'px'

    this.addEventListener('show.bs.dropdown', (event) => {
      const isMobile = window.matchMedia('(max-width: 499px').matches

      if (isMobile) {
        event.preventDefault()
        this.initOffcanvas()
      } else {
        this.productCard.querySelector('.product-card-img-wrapper').style.opacity = '.2'
      }
    })
    this.addEventListener('hide.bs.dropdown', (event) => {
      const isMobile = window.matchMedia('(max-width: 499px').matches

      if (isMobile) {
        event.preventDefault()
        this.removeOffcanvas()
      } else {
        this.productCard.querySelector('.product-card-img-wrapper').style.opacity = '1'
      }
    })

    this.querySelectorAll('.dropdown-menu button').forEach(btn => {
      btn.addEventListener('click', () => this.addToCart(btn))
    })
  }

  async addToCart (btn) {
    window.bootstrap.Dropdown.getOrCreateInstance(this).hide()

    this.dropdownBtn.classList.add('loading')
    this.dropdownBtn.disabled = true
    this.dropdownBtn.setAttribute('aria-busy', 'true')

    const items = [{
      id: Number(btn.dataset.variantId),
      quantity: 1
    }]

    await document.querySelector('cart-container').add({ items })

    this.dropdownBtn.classList.remove('loading')
    this.dropdownBtn.disabled = false
    this.dropdownBtn.setAttribute('aria-busy', 'false')
  }

  initOffcanvas () {
    const html = `
      <div id="product-dropup-select-offcanvas" class="offcanvas offcanvas-bottom" tabindex="-1" aria-labelledby="product-dropup-select-offcanvas-title">
        <div class="offcanvas-header">
          <h5 class="offcanvas-title" id="product-dropup-select-offcanvas-title">
            ${this.dropdownBtn.textContent}
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="offcanvas" aria-label="Close">
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" class="svg-icon-x" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>
        <div class="offcanvas-body p-0">
          <ul class="dropdown-menu">
            ${this.querySelector('.dropdown-menu').innerHTML}
          </ul>
        </div>
      </div>
    `
    document.body.insertAdjacentHTML('beforeend', html)

    window.bootstrap.Offcanvas.getOrCreateInstance('#product-dropup-select-offcanvas').show()

    document.querySelectorAll('#product-dropup-select-offcanvas .dropdown-menu button').forEach(btn => {
      btn.addEventListener('click', () => {
        this.addToCart(btn)
        window.bootstrap.Offcanvas.getOrCreateInstance('#product-dropup-select-offcanvas').hide()
      })
    })

    document.querySelector('#product-dropup-select-offcanvas').addEventListener('hidden.bs.offcanvas', () => {
      document.querySelector('#product-dropup-select-offcanvas').remove()
    })
  }
}
customElements.define('product-dropup-selector', ProductDropupSelector)

class CrossSells extends HTMLElement {
  constructor () {
    super()

    this.querySelectorAll('input[type="checkbox"').forEach(checkbox => {
      checkbox.addEventListener('change', () => this.onCheckboxChange(checkbox))
    })

    this.querySelectorAll('select[name="variant-id"').forEach(select => {
      select.addEventListener('change', () => this.onChangeVariant(select))
    })

    this.querySelectorAll('.btn-atc').forEach(btn => {
      btn.addEventListener('click', () => this.addToCart(btn))
    })
  }

  onChangeVariant (select) {
    const variantImage = select[select.selectedIndex].dataset.variantImage

    if (variantImage.length) {
      select.closest('.product-card').querySelector('.product-card-img-wrapper img').setAttribute('src', variantImage)
    }

    this.updateTotalPrice()
  }

  onCheckboxChange (checkbox) {
    if (checkbox.checked) {
      checkbox.closest('.product-card').setAttribute('data-is-selected', 'true')
    } else {
      checkbox.closest('.product-card').setAttribute('data-is-selected', 'false')
    }
    this.updateTotalPrice()
  }

  updateTotalPrice () {
    let totalPrice = 0
    let totalComparePrice = 0

    this.querySelectorAll('input[type="checkbox"').forEach(checkbox => {
      if (checkbox.checked) {
        const inputHidden = checkbox.closest('.product-card').querySelector('input[name="variant-id"][type="hidden"]')
        if (inputHidden) {
          totalPrice += Number(inputHidden.dataset.price)
          totalComparePrice += Number(inputHidden.dataset.compareAtPrice)
        }

        const select = checkbox.closest('.product-card').querySelector('select[name="variant-id"]')
        if (select) {
          totalPrice += Number(select[select.selectedIndex].dataset.price)
          totalComparePrice += Number(select[select.selectedIndex].dataset.compareAtPrice)
        }
      }
    })

    this.querySelectorAll('[data-total-price]').forEach(elem => {
      elem.textContent = `${window.Shopify.formatMoney(totalPrice)}`.replace('.00', '')
    })

    this.querySelectorAll('[data-total-compare-price]').forEach(elem => {
      elem.textContent = `${window.Shopify.formatMoney(totalComparePrice)}`.replace('.00', '')
    })

    this.querySelectorAll('[data-total-savings]').forEach(elem => {
      elem.textContent = `${window.Shopify.formatMoney(totalComparePrice - totalPrice)}`.replace('.00', '')
    })

    if (totalPrice === 0) {
      this.querySelector('.btn-atc').disabled = true
    } else {
      this.querySelector('.btn-atc').disabled = false
    }

    if (totalComparePrice > totalPrice) {
      this.querySelectorAll('[data-total-compare-price]').forEach(elem => {
        elem.closest('s').removeAttribute('hidden')
      })
      this.querySelectorAll('[data-total-savings]').forEach(elem => {
        elem.closest('.cross-sells-footer-total-savings').removeAttribute('hidden')
      })
    } else {
      this.querySelectorAll('[data-total-compare-price]').forEach(elem => {
        elem.closest('s').setAttribute('hidden', 'hidden')
      })
      this.querySelectorAll('[data-total-savings]').forEach(elem => {
        elem.closest('.cross-sells-footer-total-savings').setAttribute('hidden', 'hidden')
      })
    }
  }

  async addToCart (atcBtn) {
    atcBtn.style.width = `${atcBtn.clientWidth}px`
    atcBtn.classList.add('loading')
    atcBtn.disabled = true
    atcBtn.setAttribute('aria-busy', 'true')

    const items = []

    this.querySelectorAll('input[type="checkbox"').forEach(checkbox => {
      if (checkbox.checked) {
        const id = Number(checkbox.closest('.product-card').querySelector('[name="variant-id"]').value)

        items.push({
          id,
          quantity: 1
        })
      }
    })

    await document.querySelector('cart-container').add({ items })

    atcBtn.style.width = ''
    atcBtn.classList.remove('loading')
    atcBtn.disabled = false
    atcBtn.setAttribute('aria-busy', 'false')
  }
}
customElements.define('cross-sells', CrossSells)

class ProductCard extends HTMLElement {
  constructor () {
    super()

    this.handleCarouselHover()
  }

  handleCarouselHover () {
    if (window.matchMedia('(pointer: coarse)').matches) return

    const bsCarousel = window.bootstrap.Carousel.getOrCreateInstance(this.querySelector('.product-card-img-wrapper .carousel'))
    const carouselInner = this.querySelector('.product-card-img-wrapper .carousel-inner')
    let myInterval

    carouselInner.addEventListener('mouseenter', () => {
      setTimeout(() => {
        bsCarousel.next()
      }, 500)

      myInterval = setInterval(() => {
        bsCarousel.next()
      }, 2000)
    })

    carouselInner.addEventListener('mouseleave', () => {
      clearInterval(myInterval)
    })
  }
}
customElements.define('product-card', ProductCard)

class FileUploadPersonalizationField extends HTMLElement {
  constructor () {
    super()

    this.loadStyleScripts()

    const vendorScript = document.querySelector('script[src*="plugin-image-preview"]')
    vendorScript.onload = () => {
      this.init()
    }

    window.addEventListener('kt.product.quick_view.modal_shown', () => this.init())
  }

  loadStyleScripts () {
    if (document.querySelector('script[src*="plugin-image-preview"]')) return

    const style1 = document.createElement('link')
    style1.setAttribute('href', 'https://cdn.jsdelivr.net/npm/filepond@4.31.1/dist/filepond.min.css')
    style1.setAttribute('rel', 'stylesheet')
    style1.setAttribute('integrity', 'sha256-a95jYCBL4++k1XyLYgulKmY33bIJIVYMsJO/RNytaJM=')
    style1.setAttribute('crossorigin', 'anonymous')
    document.head.appendChild(style1)

    const style2 = document.createElement('link')
    style2.setAttribute('href', 'https://cdn.jsdelivr.net/npm/filepond-plugin-image-preview@4.6.12/dist/filepond-plugin-image-preview.min.css')
    style2.setAttribute('rel', 'stylesheet')
    style2.setAttribute('integrity', 'sha256-YsO8aMI20vSizdmc2mmWx2DU1wof4v60Nwy7hIBvdM8=')
    style2.setAttribute('crossorigin', 'anonymous')
    document.head.appendChild(style2)

    const script1 = document.createElement('script')
    script1.setAttribute('src', 'https://cdn.jsdelivr.net/npm/filepond@4.31.1/dist/filepond.min.js')
    script1.setAttribute('integrity', 'sha256-6yXpr8+sATA4Q2ANTyZmpn4ZGP7grbIRNpe9s0Y+iO0=')
    script1.setAttribute('crossorigin', 'anonymous')
    script1.setAttribute('defer', 'defer')
    document.head.appendChild(script1)

    const script2 = document.createElement('script')
    script2.setAttribute('src', 'https://cdn.jsdelivr.net/npm/filepond-plugin-image-preview@4.6.12/dist/filepond-plugin-image-preview.min.js')
    script2.setAttribute('integrity', 'sha256-1vQHytMrpOsKFyUUPuEVhxeNH6UhMX3uNetDRvlSpwU=')
    script2.setAttribute('crossorigin', 'anonymous')
    script1.setAttribute('defer', 'defer')
    document.head.appendChild(script2)
  }

  async init () {
    await new Promise(resolve => setTimeout(resolve, 200))

    window.FilePond.registerPlugin(window.FilePondPluginImagePreview)
    const inputElem = this.querySelector('input[type="file"]')

    window.FilePond.create(inputElem, {
      storeAsFile: true,
      labelIdle: this.querySelector('[data-label-text]').innerHTML
    })
  }
}
customElements.define('file-upload-personalization-field', FileUploadPersonalizationField)

// Product block collapses
if (window.matchMedia('(max-width: 1199px').matches) {
  document.querySelectorAll('.product-block-collapse').forEach(elem => {
    elem.querySelector('.collapse').addEventListener('shown.bs.collapse', () => {
      elem.scrollIntoView({ behavior: 'smooth' })
    })
  })
}

// Shopify Subscriptions app
const adjustShopifySubscriptionsStyling = () => {
  document.querySelectorAll('.shopify_subscriptions_app_block [type="radio"]').forEach(radio => {
    radio.classList.add('form-check-input')
  })
}
adjustShopifySubscriptionsStyling()
window.addEventListener('kt.product.quick_view.modal_shown', adjustShopifySubscriptionsStyling)

/* ==========================================================
 * LIVE METALS PRICING CALCULATION
 * ========================================================== */
document.addEventListener('DOMContentLoaded', async () => {
  const fallbackLiveRates = {
    gold_24k: 15273, // Derived
    gold_22k: 14000,
    gold_21k: 13364,
    gold_20k: 12728,
    gold_18k: 11455,
    gold_14k: 8909,
    gold_10k: 6364,
    gold_9k: 5727,
    silver_999: 286,
    silver_925: 265,
    silver: 286
  };

  let liveRates = null;

  const hasUsableLiveRates = rates =>
    rates &&
    Number(rates.gold_24k) > 0 &&
    Number(rates.silver_999 || rates.silver) > 0;

  const storeLiveRates = rates => {
    liveRates = rates;
    sessionStorage.setItem('SvadeziLiveMetalRates', JSON.stringify(liveRates));
    sessionStorage.setItem('SvadeziIsFallbackRates', window.svadeziIsFallbackRates ? 'true' : 'false');
    // BUG 1 FIX: Sync the header ticker from the same live rates used by the calculator
    updateSvadeziTicker(liveRates);
    
    // Page load race condition fix: run updateLivePrice immediately after rates are loaded
    const initialVariantInput = document.querySelector('input[name="id"]');
    if (initialVariantInput) {
      updateLivePrice(initialVariantInput.value);
    }
    
    // BUG FIX: Update all collection product cards with the live 9K / Lab Grown prices
    if (typeof window.initSvadeziCollectionPricing === 'function') {
      window.initSvadeziCollectionPricing();
    }
  };

  /**
   * BUG 1 FIX — updateSvadeziTicker
   * Pushes freshly-fetched (or cached) rates into the live-rate-ticker strip.
   * Silver 925 is displayed as silver_999 × 0.925.
   * Ticker element classes: .rate-18k, .rate-14k, .rate-9k, .rate-silver
   */
  const updateSvadeziTicker = rates => {
    const fmt = val => '₹' + Math.round(val).toLocaleString('en-IN') + '/g';
    const indic = window.svadeziIsFallbackRates ? ' <small class="opacity-75">(Indicative)</small>' : '';
    
    document.querySelectorAll('.rate-18k').forEach(el => {
      el.innerHTML = fmt(rates.gold_18k || rates.gold_24k * 0.750) + indic;
    });
    document.querySelectorAll('.rate-14k').forEach(el => {
      el.innerHTML = fmt(rates.gold_14k || rates.gold_24k * 0.585) + indic;
    });
    document.querySelectorAll('.rate-9k').forEach(el => {
      el.innerHTML = fmt(rates.gold_9k || rates.gold_24k * 0.375) + indic;
    });
    document.querySelectorAll('.rate-silver').forEach(el => {
      el.innerHTML = fmt((rates.silver_999 || rates.silver) * 0.925) + indic;
    });
  };

  const useFallbackLiveRates = (message, err) => {
    if (err) {
      console.error(message, err);
    } else {
      console.error(message);
    }
    window.svadeziIsFallbackRates = true;
    storeLiveRates(fallbackLiveRates);
  };
  
  // Try to fetch from sessionStorage first
  try {
    liveRates = JSON.parse(sessionStorage.getItem('SvadeziLiveMetalRates'));
    window.svadeziIsFallbackRates = sessionStorage.getItem('SvadeziIsFallbackRates') === 'true';
  } catch (e) {}

  if (!hasUsableLiveRates(liveRates)) {
    liveRates = null;
  }

  // BUG 1 FIX: If rates were already cached, sync the ticker immediately
  // BUG 8 FIX: Also call updateLivePrice when using cached rates (fixes Rs. 0.00 on warm sessions)
  if (liveRates) {
    updateSvadeziTicker(liveRates);
    const cachedVariantInput = document.querySelector('input[name="id"]');
    if (cachedVariantInput && cachedVariantInput.value) {
      // Defer slightly to ensure updateLivePrice is in scope after WEIGHT_REGISTRY loads
      setTimeout(() => updateLivePrice(cachedVariantInput.value), 0);
    }
  }

  if (!liveRates) {
    if (typeof window.fetchSvadeziLiveRates !== 'function') {
      useFallbackLiveRates("Proxy function window.fetchSvadeziLiveRates is missing; using fallback rates");
    } else {
      try {
        const [goldRes, silverRes] = await window.fetchSvadeziLiveRates();

        if (goldRes.ok && silverRes.ok) {
          const goldData = await goldRes.json();
          const silverData = await silverRes.json();
          const rawSilverRate = silverData.price_gram_24k || (silverData.price / 31.1034768);
          const rawGoldRate = goldData.price_gram_24k || (goldData.price / 31.1034768);
          
          const goldMarkup = 1 + ((window.SvadeziPricingConfig && window.SvadeziPricingConfig.goldMarkupPercent) || 15.43) / 100;
          const silverMarkup = 1 + ((window.SvadeziPricingConfig && window.SvadeziPricingConfig.silverMarkupPercent) || 27.85) / 100;

          const silver_999 = Math.round(rawSilverRate * silverMarkup);

          const fetchedRates = {
            gold_24k: Math.round(rawGoldRate * goldMarkup),
            gold_22k: Math.round(rawGoldRate * 0.916 * goldMarkup),
            gold_21k: Math.round(rawGoldRate * 0.875 * goldMarkup),
            gold_20k: Math.round(rawGoldRate * 0.833 * goldMarkup),
            gold_18k: Math.round(rawGoldRate * 0.75 * goldMarkup),
            gold_14k: Math.round(rawGoldRate * 0.5833 * goldMarkup),
            gold_10k: Math.round(rawGoldRate * 0.4167 * goldMarkup),
            gold_9k:  Math.round(rawGoldRate * 0.375 * goldMarkup),
            silver_999: silver_999,
            silver: silver_999,
            silver_925: Math.round(silver_999 * 0.925)
          };

          if (hasUsableLiveRates(fetchedRates)) {
            storeLiveRates(fetchedRates);
          } else {
            useFallbackLiveRates("Live rates response missing required values", JSON.stringify({ goldData, silverData }));
          }
        } else {
          useFallbackLiveRates("Failed to fetch live rates", `${await goldRes.text()} ${await silverRes.text()}`);
        }
      } catch (err) {
        useFallbackLiveRates("Error fetching live rates", err);
      }
    }
  }
  
const WEIGHT_REGISTRY = {
  "CC-ER1080": {
    "9K": 3.015,
    "14K": 3.434,
    "18K": 4.09,
    "Silver": 2.716,
    "DiamondCt": 0.4356
  },
  "CC-ER1205": {
    "9K": 1.284,
    "14K": 1.463,
    "18K": 1.742,
    "Silver": 1.157,
    "DiamondCt": 0.0715
  },
  "CDL-ER1411": {
    "9K": 3.077,
    "14K": 3.506,
    "18K": 4.175,
    "Silver": 2.772,
    "DiamondCt": 1.308
  },
  "CDL-ER1412": {
    "9K": 5.023,
    "14K": 5.045,
    "18K": 5.08,
    "Silver": 5.007,
    "DiamondCt": 1.32
  },
  "ER-0537": {
    "9K": 4.932,
    "14K": 5.599,
    "18K": 6.641,
    "Silver": 4.456,
    "DiamondCt": 0.6776
  },
  "ER-0824": {
    "9K": 5.379,
    "14K": 6.127,
    "18K": 7.296,
    "Silver": 4.845,
    "DiamondCt": 2.72
  },
  "ER-0892": {
    "9K": 2.936,
    "14K": 3.317,
    "18K": 3.913,
    "Silver": 2.664,
    "DiamondCt": 0.968
  },
  "ER-0937": {
    "9K": 1.583,
    "14K": 1.787,
    "18K": 2.106,
    "Silver": 1.437,
    "DiamondCt": 0.5782
  },
  "ER-10437": {
    "9K": 0.811,
    "14K": 0.924,
    "18K": 1.1,
    "Silver": 0.731,
    "DiamondCt": 0.54
  },
  "ER-10444": {
    "9K": 1.053,
    "14K": 1.188,
    "18K": 1.4,
    "Silver": 0.957,
    "DiamondCt": 0.4
  },
  "ER-7584": {
    "9K": 12.233,
    "14K": 13.935,
    "18K": 16.595,
    "Silver": 11.021,
    "DiamondCt": 2.6058
  },
  "ER-7711": {
    "9K": 1.213,
    "14K": 1.382,
    "18K": 1.646,
    "Silver": 1.093,
    "DiamondCt": 0.3432
  },
  "ER-7729": {
    "9K": 0.649,
    "14K": 0.734,
    "18K": 0.868,
    "Silver": 0.588,
    "DiamondCt": 0.1826
  },
  "ER-7730": {
    "9K": 5.2,
    "14K": 5.924,
    "18K": 7.054,
    "Silver": 4.685,
    "DiamondCt": 1.4696
  },
  "ER-7740": {
    "9K": 3.081,
    "14K": 3.487,
    "18K": 4.122,
    "Silver": 2.791,
    "DiamondCt": 0.8008
  },
  "ER-7747": {
    "9K": 4.701,
    "14K": 5.355,
    "18K": 6.377,
    "Silver": 4.235,
    "DiamondCt": 0.9504
  },
  "ER-7762": {
    "9K": 3.113,
    "14K": 3.546,
    "18K": 4.223,
    "Silver": 2.804,
    "DiamondCt": 0.2332
  },
  "ER-7787": {
    "9K": 1.724,
    "14K": 1.962,
    "18K": 2.334,
    "Silver": 1.555,
    "DiamondCt": 0.072
  },
  "ER-7806": {
    "9K": 1.311,
    "14K": 1.494,
    "18K": 1.779,
    "Silver": 1.181,
    "DiamondCt": 0.1925
  },
  "ER-8022": {
    "9K": 1.632,
    "14K": 1.859,
    "18K": 2.214,
    "Silver": 1.471,
    "DiamondCt": 0.2464
  },
  "ER-8292": {
    "9K": 1.609,
    "14K": 1.831,
    "18K": 2.176,
    "Silver": 1.452,
    "DiamondCt": 0.10318
  },
  "ER-8319": {
    "9K": 2.98,
    "14K": 3.387,
    "18K": 4.024,
    "Silver": 2.689,
    "DiamondCt": 0.2474
  },
  "ER-8324": {
    "9K": 1.697,
    "14K": 1.931,
    "18K": 2.298,
    "Silver": 1.53,
    "DiamondCt": 0.05896
  },
  "ER-8342": {
    "9K": 10.786,
    "14K": 12.226,
    "18K": 14.477,
    "Silver": 9.76,
    "DiamondCt": 2.17338
  },
  "ER-8590": {
    "9K": 1.429,
    "14K": 1.627,
    "18K": 1.935,
    "Silver": 1.289,
    "DiamondCt": 0.055
  },
  "ER-8706": {
    "9K": 2.168,
    "14K": 2.469,
    "18K": 2.94,
    "Silver": 1.953,
    "DiamondCt": 0.264
  },
  "ER-8840-M5": {
    "9K": 4.043,
    "14K": 4.584,
    "18K": 5.429,
    "Silver": 3.658,
    "DiamondCt": 0.7795
  },
  "ER-8889": {
    "9K": 2.631,
    "14K": 2.997,
    "18K": 3.57,
    "Silver": 2.371,
    "DiamondCt": 0.48
  },
  "FF-ER1679": {
    "9K": 2.631,
    "14K": 2.997,
    "18K": 3.57,
    "Silver": 2.371,
    "DiamondCt": 0.5666
  },
  "FF-ER1711": {
    "9K": 4.103,
    "14K": 4.674,
    "18K": 5.566,
    "Silver": 3.696,
    "DiamondCt": 2.4,
    "Solitaire": true
    // BUG 2: Large centre stone — 2.4 ct solitaire; natural variants use ₹185,000/ct rate
  },
  "MER-10068": {
    "9K": 0.869,
    "14K": 0.99,
    "18K": 1.179,
    "Silver": 0.783,
    "DiamondCt": 0.09
  },
  "MER-10431": {
    "9K": 0.843,
    "14K": 0.961,
    "18K": 1.144,
    "Silver": 0.76,
    "DiamondCt": 1
  },
  "MER-10436": {
    "9K": 1.095,
    "14K": 1.247,
    "18K": 1.485,
    "Silver": 0.986,
    "DiamondCt": 0.27
  },
  "MER-10438": {
    "9K": 0.892,
    "14K": 1.016,
    "18K": 1.21,
    "Silver": 0.804,
    "DiamondCt": 0
  },
  "MER-10439": {
    "9K": 1.03,
    "14K": 1.173,
    "18K": 1.397,
    "Silver": 0.928,
    "DiamondCt": 0.07
  },
  "MER-10442": {
    "9K": 1.182,
    "14K": 1.342,
    "18K": 1.592,
    "Silver": 1.068,
    "DiamondCt": 0.17
  },
  "MER-10443": {
    "9K": 0.957,
    "14K": 1.09,
    "18K": 1.298,
    "Silver": 0.862,
    "DiamondCt": 0.15
  },
  "MER-10447": {
    "9K": 1.484,
    "14K": 1.69,
    "18K": 2.013,
    "Silver": 1.337,
    "DiamondCt": 0.97
  },
  "MER-10448": {
    "9K": 0.673,
    "14K": 0.767,
    "18K": 0.913,
    "Silver": 0.606,
    "DiamondCt": 0.22
  },
  "MER-10455": {
    "9K": 0.811,
    "14K": 0.924,
    "18K": 1.1,
    "Silver": 0.731,
    "DiamondCt": 0.25
  },
  "MER-7708": {
    "9K": 1.365,
    "14K": 1.542,
    "18K": 1.82,
    "Silver": 1.238,
    "DiamondCt": 0.4356
  },
  "MER-7756": {
    "9K": 0.38,
    "14K": 0.427,
    "18K": 0.5,
    "Silver": 0.347,
    "DiamondCt": 0.23
  },
  "MER-7959": {
    "9K": 1.253,
    "14K": 1.425,
    "18K": 1.693,
    "Silver": 1.131,
    "DiamondCt": 0.099
  },
  "MER-7963": {
    "9K": 0.571,
    "14K": 0.648,
    "18K": 0.768,
    "Silver": 0.516,
    "DiamondCt": 0.077
  },
  "MER-7991": {
    "9K": 2.186,
    "14K": 2.49,
    "18K": 2.965,
    "Silver": 1.969,
    "DiamondCt": 0.19
  },
  "MER-8351": {
    "9K": 0.388,
    "14K": 0.436,
    "18K": 0.512,
    "Silver": 0.353,
    "DiamondCt": 0.2
  },
  "MER-867": {
    "9K": 0.737,
    "14K": 0.834,
    "18K": 0.986,
    "Silver": 0.667,
    "DiamondCt": 0.1782
  },
  "MER-8918": {
    "9K": 0.681,
    "14K": 0.776,
    "18K": 0.924,
    "Silver": 0.614,
    "DiamondCt": 0.82
  },
  "MER-8979": {
    "9K": 2.438,
    "14K": 2.773,
    "18K": 3.297,
    "Silver": 2.199,
    "DiamondCt": 0.1386
  },
  "MER-9184": {
    "9K": 0.966,
    "14K": 1.1,
    "18K": 1.31,
    "Silver": 0.87,
    "DiamondCt": 1.5
  },
  "MER-9390": {
    "9K": 1.014,
    "14K": 1.155,
    "18K": 1.375,
    "Silver": 0.913,
    "DiamondCt": 0.2
  },
  "FF-GR1078": {
    "9K": 2.927,
    "14K": 3.797,
    "18K": 5.386,
    "Silver": 2.927,
    "DiamondCt": 0.25
  },
  "FF-GR1081": {
    "9K": 3.319,
    "14K": 4.306,
    "18K": 6.106,
    "Silver": 3.319,
    "DiamondCt": 0
  },
  "FF-GR1082": {
    "9K": 3.896,
    "14K": 5.055,
    "18K": 7.168,
    "Silver": 3.896,
    "DiamondCt": 0.745
  },
  "FF-GR1097": {
    "9K": 2.97,
    "14K": 3.854,
    "18K": 5.465,
    "Silver": 2.97,
    "DiamondCt": 0.205
  },
  "FF-GR1166": {
    "9K": 3.147,
    "14K": 4.085,
    "18K": 5.793,
    "Silver": 3.147,
    "DiamondCt": 0.25
  },
  "FF-GR1177": {
    "9K": 3.639,
    "14K": 4.722,
    "18K": 6.697,
    "Silver": 3.639,
    "DiamondCt": 0.77
  },
  "FF-GR1178": {
    "9K": 4.168,
    "14K": 5.409,
    "18K": 7.671,
    "Silver": 4.168,
    "DiamondCt": 0.2875
  },
  "FF-GR1182": {
    "9K": 5.466,
    "14K": 7.092,
    "18K": 10.059,
    "Silver": 5.466,
    "DiamondCt": 0.2002
  },
  "FF-GR1185": {
    "9K": 4.715,
    "14K": 6.118,
    "18K": 8.675,
    "Silver": 4.715,
    "DiamondCt": 0.3025
  },
  "FF-GR1188": {
    "9K": 4.166,
    "14K": 5.407,
    "18K": 7.666,
    "Silver": 4.166,
    "DiamondCt": 0.5
  },
  "FF-GR1196": {
    "9K": 4.129,
    "14K": 5.358,
    "18K": 7.598,
    "Silver": 4.129,
    "DiamondCt": 0.0858
  },
  "GR-0341": {
    "9K": 3.496,
    "14K": 4.52,
    "18K": 6.385,
    "Silver": 3.496,
    "DiamondCt": 0.29
  },
  "GR-0463": {
    "9K": 3.105,
    "14K": 4.028,
    "18K": 5.713,
    "Silver": 3.105,
    "DiamondCt": 0.2926
  },
  "GR-0917": {
    "9K": 5.619,
    "14K": 7.274,
    "18K": 10.295,
    "Silver": 5.619,
    "DiamondCt": 0.2695
  },
  "GR-0962": {
    "9K": 4.742,
    "14K": 6.128,
    "18K": 8.653,
    "Silver": 4.742,
    "DiamondCt": 0.4356
  },
  "GR-0987": {
    "9K": 4.024,
    "14K": 5.221,
    "18K": 7.404,
    "Silver": 4.024,
    "DiamondCt": 0.099
  },
  "GR-23": {
    "9K": 2.337,
    "14K": 3.023,
    "18K": 4.273,
    "Silver": 2.337,
    "DiamondCt": 0.16
  },
  "GR-3244": {
    "9K": 3.492,
    "14K": 4.532,
    "18K": 6.427,
    "Silver": 3.492,
    "DiamondCt": 0.2871
  },
  "GR-7990": {
    "9K": 2.845,
    "14K": 3.692,
    "18K": 5.237,
    "Silver": 2.845,
    "DiamondCt": 0.2452
  },
  "GR-8012": {
    "9K": 4.487,
    "14K": 5.821,
    "18K": 8.256,
    "Silver": 4.487,
    "DiamondCt": 0
  },
  "GR-8083": {
    "9K": 7.121,
    "14K": 9.241,
    "18K": 13.104,
    "Silver": 7.121,
    "DiamondCt": 4.4
  },
  "GR-8084": {
    "9K": 6.699,
    "14K": 8.693,
    "18K": 12.328,
    "Silver": 6.699,
    "DiamondCt": 2.8624
  },
  "GR-8507": {
    "9K": 3.347,
    "14K": 4.342,
    "18K": 6.159,
    "Silver": 3.347,
    "DiamondCt": 0.3663
  },
  "GR-8682": {
    "9K": 4.461,
    "14K": 5.789,
    "18K": 8.208,
    "Silver": 4.461,
    "DiamondCt": 0.7084
  },
  "BND-0244": {
    "9K": 3.458,
    "14K": 4.487,
    "18K": 6.362,
    "Silver": 3.458,
    "DiamondCt": 0.5346
  },
  "BND-0649": {
    "9K": 2.769,
    "14K": 3.593,
    "18K": 5.094,
    "Silver": 2.769,
    "DiamondCt": 0.3881
  },
  "BOZ-LR114": {
    "9K": 0.742,
    "14K": 0.917,
    "18K": 1.238,
    "Silver": 0.742,
    "DiamondCt": 0.75
  },
  "BOZ-LR1254": {
    "9K": 1.999,
    "14K": 2.594,
    "18K": 3.678,
    "Silver": 1.999,
    "DiamondCt": 1.252
  },
  "CC-LR1238": {
    "9K": 2.04,
    "14K": 2.649,
    "18K": 3.756,
    "Silver": 2.04,
    "DiamondCt": 0.684
  },
  "CDL-212": {
    "9K": 5.05,
    "14K": 6.534,
    "18K": 9.237,
    "Silver": 5.05,
    "DiamondCt": 0.32
  },
  "CDL-2385": {
    "9K": 1.547,
    "14K": 2.008,
    "18K": 2.847,
    "Silver": 1.547,
    "DiamondCt": 0.014
  },
  "CDL-3611": {
    "9K": 4.359,
    "14K": 5.655,
    "18K": 8.02,
    "Silver": 4.359,
    "DiamondCt": 0.4
  },
  "CDL-4304": {
    "9K": 3.845,
    "14K": 4.604,
    "18K": 5.989,
    "Silver": 3.845,
    "DiamondCt": 6.46
  },
  "CDL-4320": {
    "9K": 3.212,
    "14K": 4.168,
    "18K": 5.91,
    "Silver": 3.212,
    "DiamondCt": 0
  },
  "CDL-4330": {
    "9K": 3.866,
    "14K": 5.018,
    "18K": 7.116,
    "Silver": 3.866,
    "DiamondCt": 1.23
  },
  "CDL-5350": {
    "9K": 2.635,
    "14K": 3.42,
    "18K": 4.849,
    "Silver": 2.635,
    "DiamondCt": 0.71
  },
  "CDL-5355": {
    "9K": 3.41,
    "14K": 4.391,
    "18K": 6.181,
    "Silver": 3.41,
    "DiamondCt": 0.55
  },
  "CDL-5362": {
    "9K": 2.243,
    "14K": 2.912,
    "18K": 4.128,
    "Silver": 2.243,
    "DiamondCt": 0.64
  },
  "CDL-8155": {
    "9K": 1.937,
    "14K": 2.514,
    "18K": 3.564,
    "Silver": 1.937,
    "DiamondCt": 0
  },
  "CDL-8555": {
    "9K": 0.904,
    "14K": 1.174,
    "18K": 1.664,
    "Silver": 0.904,
    "DiamondCt": 1.2844
  },
  "FF-LR1572": {
    "9K": 1.979,
    "14K": 2.569,
    "18K": 3.643,
    "Silver": 1.979,
    "DiamondCt": 0.53
  },
  "FF-LR1618": {
    "9K": 1.567,
    "14K": 2.033,
    "18K": 2.883,
    "Silver": 1.567,
    "DiamondCt": 0.3813
  },
  "FF-LR1703": {
    "9K": 2.588,
    "14K": 3.359,
    "18K": 4.762,
    "Silver": 2.588,
    "DiamondCt": 0.902
  },
  "FF-LR1710": {
    "9K": 2.891,
    "14K": 3.751,
    "18K": 5.321,
    "Silver": 2.891,
    "DiamondCt": 0.6056
  },
  "FF-LR1719": {
    "9K": 2.193,
    "14K": 2.846,
    "18K": 4.036,
    "Silver": 2.193,
    "DiamondCt": 0.1666
  },
  "FF-LR1832": {
    "9K": 2.543,
    "14K": 3.3,
    "18K": 4.681,
    "Silver": 2.543,
    "DiamondCt": 2.088
  },
  "GR-8895": {
    "9K": 2.849,
    "14K": 3.697,
    "18K": 5.241,
    "Silver": 2.849,
    "DiamondCt": 0.36
  },
  "GR-8951": {
    "9K": 3.577,
    "14K": 4.622,
    "18K": 6.525,
    "Silver": 3.577,
    "DiamondCt": 0.35325
  },
  "SP-BND011": {
    "9K": 5.981,
    "14K": 7.761,
    "18K": 11.005,
    "Silver": 5.981,
    "DiamondCt": 0.035
  },
  "SP-BND121": {
    "9K": 4.123,
    "14K": 5.35,
    "18K": 7.588,
    "Silver": 4.123,
    "DiamondCt": 0.175
  },
  "CDL-LR8357": {
    "9K": 4.655,
    "14K": 5.205,
    "18K": 6.065,
    "Silver": 4.263,
    "DiamondCt": 3.5
  },
  "CDL-MS0411": {
    "9K": 3.658,
    "14K": 4.166,
    "18K": 4.96,
    "Silver": 3.296,
    "DiamondCt": 0.03
  },
  "CDL-MS1006": {
    "9K": 2.091,
    "14K": 2.379,
    "18K": 2.829,
    "Silver": 1.886,
    "DiamondCt": 0.1
  },
  "CDL-MS1008": {
    "9K": 2.905,
    "14K": 3.298,
    "18K": 3.913,
    "Silver": 2.625,
    "DiamondCt": 0.39
  },
  "CDL-MS1009": {
    "9K": 2.892,
    "14K": 3.293,
    "18K": 3.919,
    "Silver": 2.606,
    "DiamondCt": 0.05
  },
  "CDL-MS1010": {
    "9K": 2.901,
    "14K": 3.303,
    "18K": 3.931,
    "Silver": 2.615,
    "DiamondCt": 0.06
  },
  "CDL-MS1011": {
    "9K": 3.146,
    "14K": 3.54,
    "18K": 4.155,
    "Silver": 2.865,
    "DiamondCt": 1.58
  },
  "CDL-MS1012": {
    "9K": 2.231,
    "14K": 2.528,
    "18K": 2.991,
    "Silver": 2.02,
    "DiamondCt": 0.51
  },
  "CDL-MS1013": {
    "9K": 2.755,
    "14K": 3.126,
    "18K": 3.706,
    "Silver": 2.491,
    "DiamondCt": 0.44
  },
  "CDL-MS1014": {
    "9K": 2.814,
    "14K": 3.202,
    "18K": 3.81,
    "Silver": 2.537,
    "DiamondCt": 0.1
  },
  "CDL-MS1015": {
    "9K": 2.677,
    "14K": 3.049,
    "18K": 3.629,
    "Silver": 2.413,
    "DiamondCt": 0.04
  },
  "CDL-MS1016": {
    "9K": 2.784,
    "14K": 3.171,
    "18K": 3.775,
    "Silver": 2.509,
    "DiamondCt": 0.03
  },
  "CDL-MS1017": {
    "9K": 2.773,
    "14K": 3.133,
    "18K": 3.696,
    "Silver": 2.516,
    "DiamondCt": 0.91
  },
  "CDL-MS1018": {
    "9K": 3.008,
    "14K": 3.412,
    "18K": 4.043,
    "Silver": 2.72,
    "DiamondCt": 0.52
  },
  "CDL-MS1019": {
    "9K": 4.501,
    "14K": 5.097,
    "18K": 6.028,
    "Silver": 4.077,
    "DiamondCt": 1.09
  },
  "CDL-MS1020": {
    "9K": 3.683,
    "14K": 4.183,
    "18K": 4.964,
    "Silver": 3.327,
    "DiamondCt": 0.46
  },
  "CDL-MS1021": {
    "9K": 3.295,
    "14K": 3.725,
    "18K": 4.396,
    "Silver": 2.989,
    "DiamondCt": 1.04
  },
  "CDL-MS1022": {
    "9K": 3.767,
    "14K": 4.276,
    "18K": 5.071,
    "Silver": 3.404,
    "DiamondCt": 0.54
  },
  "CDL-MS1023": {
    "9K": 3.098,
    "14K": 3.527,
    "18K": 4.197,
    "Silver": 2.792,
    "DiamondCt": 0.07
  },
  "CDL-MS1029": {
    "9K": 4.023,
    "14K": 4.563,
    "18K": 5.406,
    "Silver": 3.639,
    "DiamondCt": 0.72
  },
  "CDL-MS1030": {
    "9K": 3.408,
    "14K": 3.882,
    "18K": 4.622,
    "Silver": 3.07,
    "DiamondCt": 0.01
  },
  "CDL-MS1031": {
    "9K": 2.726,
    "14K": 3.088,
    "18K": 3.654,
    "Silver": 2.468,
    "DiamondCt": 0.61
  },
  "CDL-MS1033": {
    "9K": 2.874,
    "14K": 3.259,
    "18K": 3.862,
    "Silver": 2.599,
    "DiamondCt": 0.51
  },
  "CDL-MS2614": {
    "9K": 6.072,
    "14K": 6.859,
    "18K": 8.09,
    "Silver": 5.51,
    "DiamondCt": 2.05
  },
  "CDL-MS2615": {
    "9K": 2.977,
    "14K": 3.373,
    "18K": 3.992,
    "Silver": 2.694,
    "DiamondCt": 0.64
  },
  "CDL-MS2829": {
    "9K": 2.49,
    "14K": 2.822,
    "18K": 3.342,
    "Silver": 2.253,
    "DiamondCt": 0.5
  },
  "CDL-MS2832": {
    "9K": 3.165,
    "14K": 3.574,
    "18K": 4.212,
    "Silver": 2.874,
    "DiamondCt": 1.14
  },
  "CDL-MS2835": {
    "9K": 2.535,
    "14K": 2.86,
    "18K": 3.368,
    "Silver": 2.304,
    "DiamondCt": 1
  },
  "CDL-MS2857": {
    "9K": 3.162,
    "14K": 3.579,
    "18K": 4.231,
    "Silver": 2.865,
    "DiamondCt": 0.83
  },
  "CDL-MS2879": {
    "9K": 3.506,
    "14K": 3.987,
    "18K": 4.738,
    "Silver": 3.163,
    "DiamondCt": 0.24
  },
  "CDL-MS7574": {
    "9K": 5.375,
    "14K": 6.093,
    "18K": 7.214,
    "Silver": 4.864,
    "DiamondCt": 1.08
  },
  "CDL-MS7575": {
    "9K": 3.66,
    "14K": 4.167,
    "18K": 4.959,
    "Silver": 3.299,
    "DiamondCt": 0.08
  },
  "CDL-MS7576": {
    "9K": 4.837,
    "14K": 5.482,
    "18K": 6.489,
    "Silver": 4.378,
    "DiamondCt": 1.02
  },
  "CDL-MS7914-A": {
    "9K": 9.678,
    "14K": 10.913,
    "18K": 12.843,
    "Silver": 8.798,
    "DiamondCt": 4
  },
  "CDL-MS7914-B": {
    "9K": 11.053,
    "14K": 12.478,
    "18K": 14.705,
    "Silver": 10.037,
    "DiamondCt": 4.04
  },
  "CDL-MS8265": {
    "9K": 4.732,
    "14K": 5.374,
    "18K": 6.377,
    "Silver": 4.275,
    "DiamondCt": 0.594
  },
  "NP-0003": {
    "9K": 0.179,
    "14K": 0.203,
    "18K": 0.242,
    "Silver": 0.162,
    "DiamondCt": 0.0154
  },
  "NP-0013": {
    "9K": 0.437,
    "14K": 0.498,
    "18K": 0.593,
    "Silver": 0.394,
    "DiamondCt": 0.07
  },
  "NP-0024": {
    "9K": 0.241,
    "14K": 0.274,
    "18K": 0.327,
    "Silver": 0.217,
    "DiamondCt": 0.0385
  },
  "NP-0044": {
    "9K": 0.29,
    "14K": 0.329,
    "18K": 0.391,
    "Silver": 0.262,
    "DiamondCt": 0.0385
  },
  "NP-0088": {
    "9K": 0.174,
    "14K": 0.197,
    "18K": 0.232,
    "Silver": 0.158,
    "DiamondCt": 0.066
  },
  "NP-0095": {
    "9K": 0.232,
    "14K": 0.263,
    "18K": 0.313,
    "Silver": 0.209,
    "DiamondCt": 0.0275
  },
  "NP-0202": {
    "9K": 0.236,
    "14K": 0.268,
    "18K": 0.318,
    "Silver": 0.213,
    "DiamondCt": 0.02145
  },
  "NP-0203": {
    "9K": 0.321,
    "14K": 0.366,
    "18K": 0.436,
    "Silver": 0.289,
    "DiamondCt": 0.0319
  },
  "NP-0281": {
    "9K": 0.309,
    "14K": 0.352,
    "18K": 0.418,
    "Silver": 0.279,
    "DiamondCt": 0.0231
  },
  "NP-0283": {
    "9K": 0.483,
    "14K": 0.549,
    "18K": 0.652,
    "Silver": 0.436,
    "DiamondCt": 0.05005
  },
  "NP-0284": {
    "9K": 0.474,
    "14K": 0.538,
    "18K": 0.638,
    "Silver": 0.428,
    "DiamondCt": 0.066
  },
  "NP-0359": {
    "9K": 0.954,
    "14K": 1.087,
    "18K": 1.295,
    "Silver": 0.86,
    "DiamondCt": 0.25
  },
  "NP-0362": {
    "9K": 0.721,
    "14K": 0.816,
    "18K": 0.964,
    "Silver": 0.654,
    "DiamondCt": 0.209
  },
  "NP-0751": {
    "9K": 0.188,
    "14K": 0.213,
    "18K": 0.252,
    "Silver": 0.17,
    "DiamondCt": 0.0385
  },
  "NP-0994": {
    "9K": 0.413,
    "14K": 0.468,
    "18K": 0.555,
    "Silver": 0.373,
    "DiamondCt": 0.066
  },
  "NP-3990": {
    "9K": 0.28,
    "14K": 0.318,
    "18K": 0.378,
    "Silver": 0.253,
    "DiamondCt": 0.0286
  },
  "NP-4178": {
    "9K": 0.145,
    "14K": 0.164,
    "18K": 0.194,
    "Silver": 0.132,
    "DiamondCt": 0.044
  },
  "NP-7345": {
    "9K": 0.236,
    "14K": 0.268,
    "18K": 0.319,
    "Silver": 0.213,
    "DiamondCt": 0.02145
  },
  "NP-7716": {
    "9K": 0.736,
    "14K": 0.831,
    "18K": 0.979,
    "Silver": 0.668,
    "DiamondCt": 0.165
  },
  "NP-7913": {
    "9K": 0.326,
    "14K": 0.37,
    "18K": 0.439,
    "Silver": 0.294,
    "DiamondCt": 0.0385
  },
  "NP-8351": {
    "9K": 0.392,
    "14K": 0.447,
    "18K": 0.532,
    "Silver": 0.354,
    "DiamondCt": 0.209
  },
  "NP-8566": {
    "9K": 0.351,
    "14K": 0.398,
    "18K": 0.471,
    "Silver": 0.317,
    "DiamondCt": 0.066
  },
  "NP-8589": {
    "9K": 0.224,
    "14K": 0.253,
    "18K": 0.299,
    "Silver": 0.203,
    "DiamondCt": 0.0572
  }
};

  function updateLivePrice(variantId) {
    if (!variantId || !liveRates || !window.SvadeziMetalData || !window.SvadeziMetalData[variantId]) return;

    const mainPriceFinal = document.querySelector('.product-price-final');
    const mainPriceCompare = document.querySelector('.product-price-compare');
    const mainSaleBadge = document.querySelector('.savings-badge');

    const data = window.SvadeziMetalData[variantId];
    let grams = parseFloat(data.metal_weight_g) || parseFloat(data.product_metal_weight_g) || 0;
    let carats = parseFloat(data.diamond_weight_ct) || parseFloat(data.product_diamond_weight_ct) || 0;
    const isSilver = data.title.includes('silver') || data.title.includes('925');

    // Registry Fallback for Metal Weight
    if (grams === 0 && data.sku) {
      let baseSku = data.sku.split('-').slice(0, 2).join('-');
      if (data.sku.startsWith('CDL-ER') || data.sku.startsWith('FF-ER') || data.sku.startsWith('FF-GR') || data.sku.startsWith('FF-LR') || data.sku.startsWith('CDL-MS') || data.sku.startsWith('CDL-LR') || data.sku.startsWith('SP-BND')) {
        baseSku = data.sku.split('-').slice(0, 2).join('-');
      } else if (data.sku.startsWith('MER') || data.sku.startsWith('ER') || data.sku.startsWith('GR') || data.sku.startsWith('NP') || data.sku.startsWith('BND') || data.sku.startsWith('CC')) {
        baseSku = data.sku.split('-').slice(0, 2).join('-');
      }
      
      const regEntry = WEIGHT_REGISTRY[data.sku] || WEIGHT_REGISTRY[baseSku];
      if (regEntry) {
        if (isSilver) {
          grams = regEntry["Silver"];
        } else if (data.title.includes('18k') || data.title.includes('18kt')) {
          grams = regEntry["18K"];
        } else if (data.title.includes('14k') || data.title.includes('14kt')) {
          grams = regEntry["14K"];
        } else {
          grams = regEntry["9K"];
        }
      }
    }

    // Registry Fallback for Diamond Ct
    if (carats === 0 && data.sku) {
      let baseSku = data.sku.split('-').slice(0, 2).join('-');
      if (data.sku.startsWith('CDL-ER') || data.sku.startsWith('FF-ER') || data.sku.startsWith('FF-GR') || data.sku.startsWith('FF-LR') || data.sku.startsWith('CDL-MS') || data.sku.startsWith('CDL-LR') || data.sku.startsWith('SP-BND')) {
        baseSku = data.sku.split('-').slice(0, 2).join('-');
      } else if (data.sku.startsWith('MER') || data.sku.startsWith('ER') || data.sku.startsWith('GR') || data.sku.startsWith('NP') || data.sku.startsWith('BND') || data.sku.startsWith('CC')) {
        baseSku = data.sku.split('-').slice(0, 2).join('-');
      }
      
      const regEntry = WEIGHT_REGISTRY[data.sku] || WEIGHT_REGISTRY[baseSku];
      if (regEntry && regEntry["DiamondCt"] > 0) {
        carats = regEntry["DiamondCt"];
      }
    }
    
    // Determine live metal rate based on title/purity
    // Determine live metal rate based on title/purity
    let liveRate = 0;

    if (isSilver) {
      liveRate = (liveRates.silver_999 || (liveRates.gold_24k / 70)) * 0.925;
    } else {
      if (data.title.includes('14k') || data.title.includes('14kt')) {
        liveRate = liveRates.gold_14k || (liveRates.gold_24k * 0.585);
      } else if (data.title.includes('18k') || data.title.includes('18kt')) {
        liveRate = liveRates.gold_18k || (liveRates.gold_24k * 0.750);
      } else if (data.title.includes('22k') || data.title.includes('22kt')) {
        liveRate = liveRates.gold_22k || (liveRates.gold_24k * 0.916);
      } else if (data.title.includes('9k') || data.title.includes('9kt')) {
        liveRate = liveRates.gold_9k || (liveRates.gold_24k * 0.375);
      } else {
        liveRate = liveRates.gold_24k;
      }
    }

    const metal_cost = grams * liveRate;
    
    // Making charges
    const config = window.SvadeziPricingConfig || {};
    const globalVaPercent = config.vaPercent || 10;
    const variantVaPercent = parseFloat(data.making_pct);
    const vaPercentToUse = (variantVaPercent > 0) ? variantVaPercent : globalVaPercent;
    const making = metal_cost * (vaPercentToUse / 100);

    // Diamond cost logic
    let stoneType = data.stone_type || 'lab';
    const titleLower = (data.title || '').toLowerCase();
    
    // BUG 4 FIX: Derive stone type from option values, not from data.stone_type initially if options exist
    const optionEls = document.querySelectorAll('.svadezi-product-options [data-option-val].active');
    let derivedStoneType = stoneType;
    optionEls.forEach(optEl => {
      const v = (optEl.getAttribute('data-option-val') || '').toLowerCase();
      if (v.includes('natural')) derivedStoneType = 'natural';
      else if (v.includes('lab') || v.includes('grown') || v.includes('moissanite')) derivedStoneType = 'lab';
    });
    
    // Override with title if it explicitly says
    if (titleLower.includes('natural')) {
      derivedStoneType = 'natural';
    } else if (titleLower.includes('lab') || titleLower.includes('grown') || titleLower.includes('moissanite')) {
      derivedStoneType = 'lab';
    }
    
    // Force lab for known moissanite SKUs
    if (derivedStoneType !== 'natural' && data.sku && (data.sku.startsWith('ER-7711') || data.sku.startsWith('FF-ER1679'))) {
      derivedStoneType = 'lab';
    }

    let diamond_rate = config.defaultStoneRate || 35000;
    if (derivedStoneType === 'natural') {
      diamond_rate = config.naturalDiamondRate || 160000;
    } else if (derivedStoneType === 'lab') {
      diamond_rate = config.labDiamondRate || 35000;
    }
    stoneType = derivedStoneType;


    const isDiamondProduct = data.title.includes('diamond');
    const breakdownEl = document.querySelector('.product-price-breakdown');
    
    // Check if it's a diamond product missing carat weights
    if (isDiamondProduct && carats === 0) {
      if (mainPriceFinal) {
        mainPriceFinal.innerHTML = 'Price on Request';
      }
      if (mainPriceCompare) {
        mainPriceCompare.setAttribute('hidden', '');
      }
      if (mainSaleBadge) {
        mainSaleBadge.setAttribute('hidden', '');
      }
      if (breakdownEl) breakdownEl.style.display = 'none';
      return; // Stop further pricing logic
    } else {
      if (breakdownEl) breakdownEl.style.display = 'block';
    }

    const diamond_cost = carats * diamond_rate;
    
    // Base cost
    const subtotal = metal_cost + making + 800 + diamond_cost;
    
    // Final selling price
    const price = Math.round((subtotal * 1.03) / 10) * 10;
    // MRP
    const compare_at = Math.round((price * 1.50) / 10) * 10;

    // Update DOM
    const formatter = new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });

    const finalPriceEls = document.querySelectorAll('.product-price-final');
    const comparePriceEls = document.querySelectorAll('.product-price-compare');

    // BUG 4 FIX: Update dynamic labels OUTSIDE forEach so they always update regardless of price element count
    const metalLabelEl = document.querySelector('[data-metal-label]');
    if (metalLabelEl) {
      if (isSilver) {
        metalLabelEl.textContent = 'Silver Value';
      } else {
        let purityText = '9K';
        if (data.title.includes('18k') || data.title.includes('18kt')) purityText = '18K';
        else if (data.title.includes('14k') || data.title.includes('14kt')) purityText = '14K';
        else if (data.title.includes('22k') || data.title.includes('22kt')) purityText = '22K';
        metalLabelEl.textContent = `Gold Value (${purityText})`;
      }
    }

    const stoneLabelEl = document.querySelector('[data-stone-label]');
    if (stoneLabelEl) {
      // BUG 4 FIX: Derive stone type from option values, not from data.stone_type
      const optionEls = document.querySelectorAll('.svadezi-product-options [data-option-val].active');
      let derivedStoneType = stoneType; // fallback to already-computed stoneType
      optionEls.forEach(optEl => {
        const v = (optEl.getAttribute('data-option-val') || '').toLowerCase();
        if (v.includes('natural')) derivedStoneType = 'natural';
        else if (v.includes('lab') || v.includes('grown') || v.includes('moissanite')) derivedStoneType = 'lab';
      });
      if (derivedStoneType === 'lab') {
        stoneLabelEl.textContent = 'Diamond Value (Lab Grown)';
      } else {
        stoneLabelEl.textContent = 'Diamond Value (Natural)';
      }
    }

    // Update EMI and Sticky Bar price outside the loop too
    document.querySelectorAll('.emi-amount-value').forEach(emiEl => {
      emiEl.innerHTML = formatter.format(Math.round(price / 12));
    });
    // Update Sticky Bar price — used by the SVadezi mobile sticky CTA via svadezi:price-updated event
    document.querySelectorAll('[data-sticky-price]').forEach(stickyEl => {
      stickyEl.innerHTML = formatter.format(price);
    });
    // Update Diamond spec tile carat weight
    const diamondSpecEl = document.querySelector('[data-spec-diamond]');
    if (diamondSpecEl) {
      diamondSpecEl.textContent = `${carats.toFixed(2)} ct`;
    }

    // Update breakdown final price
    const finalPriceSpan = document.querySelector('.breakdown-final-price');
    if (finalPriceSpan) {
      finalPriceSpan.textContent = formatter.format(price);
    }

    finalPriceEls.forEach(el => {
      const amountSpan = el.querySelector('.live-price-amount');
      if (amountSpan) {
        amountSpan.innerHTML = formatter.format(price);
      } else {
        const moneySpan = el.querySelector('.money');
        if (moneySpan) {
          moneySpan.innerHTML = formatter.format(price);
        } else {
          el.innerHTML = formatter.format(price);
        }
      }
    });

    comparePriceEls.forEach(el => {
      const amountSpan = el.querySelector('.live-price-amount');
      if (amountSpan) {
        amountSpan.innerHTML = formatter.format(compare_at);
      } else {
        const moneySpan = el.querySelector('.money');
        if (moneySpan) {
          moneySpan.innerHTML = formatter.format(compare_at);
        } else {
          el.innerHTML = formatter.format(compare_at);
        }
      }
      el.hidden = false;
    });

    // Update breakdown card rows
    const metalCostEl = document.querySelector('[data-value="metal"]');
    const metalDetailsEl = document.querySelector('[data-details="metal"]');

    const makingCostEl = document.querySelector('[data-value="making"]');
    const makingDetailsEl = document.querySelector('[data-details="making"]');

    const diamondCostEl = document.querySelector('[data-value="stone"]');
    const diamondDetailsEl = document.querySelector('[data-details="stone"]');

    const gstCostEl = document.querySelector('[data-value="gst"]');
    const finalCostEl = document.querySelector('[data-value="final"]');
    const subtotalCostEl = document.querySelector('[data-value="subtotal"]');

    const gst_amount = Math.round(subtotal * 0.03);

    // Update metal row
    const indic = window.svadeziIsFallbackRates ? ' <small class="opacity-75">(Indicative)</small>' : '';
    if (metalCostEl) metalCostEl.innerHTML = formatter.format(metal_cost);
    if (metalDetailsEl) {
      metalDetailsEl.innerHTML = `${grams}g &times; ${formatter.format(liveRate)}/g${indic}`;
    }

    // Update making row
    const makingLabelEl = document.querySelector('[data-making-label]');
    if (makingLabelEl) {
      makingLabelEl.textContent = `VA ${vaPercentToUse}% of metal value`;
    }
    if (makingCostEl) makingCostEl.innerHTML = formatter.format(making);
    if (makingDetailsEl) {
      makingDetailsEl.innerHTML = `${vaPercentToUse}% of ${formatter.format(metal_cost)}`;
    }

    // Update diamond row
    if (diamondCostEl) diamondCostEl.innerHTML = formatter.format(diamond_cost);
    if (diamondDetailsEl) {
      diamondDetailsEl.innerHTML = `${carats}ct &times; ${formatter.format(diamond_rate)}/ct`;
    }

    const finishingCostEl = document.querySelector('[data-value="finishing"]');
    if (finishingCostEl) finishingCostEl.innerHTML = formatter.format(800);

    // Update GST and Final rows (reconcile GST exactly so 5 rows sum to TOTAL to the rupee)
    const reconciled_gst = price - (metal_cost + making + 800 + diamond_cost);
    if (gstCostEl) gstCostEl.innerHTML = formatter.format(reconciled_gst);
    if (finalCostEl) finalCostEl.innerHTML = formatter.format(price);
    if (subtotalCostEl) subtotalCostEl.innerHTML = formatter.format(subtotal);

    // Update Product Details Accordion items dynamically
    const diamondDetailLi = document.querySelector('[data-detail-diamond]');
    if (diamondDetailLi) {
      let displayStoneType = 'Diamond';
      if (data.title) {
        if (data.title.toLowerCase().includes('moissanite')) {
          displayStoneType = 'Moissanite';
        } else if (data.title.toLowerCase().includes('lab') || data.title.toLowerCase().includes('grown')) {
          displayStoneType = 'Lab Grown Diamond';
        } else if (data.title.toLowerCase().includes('natural')) {
          displayStoneType = 'Natural Diamond';
        }
      }
      diamondDetailLi.innerHTML = `<strong>Diamond:</strong> ${carats.toFixed(2)} ct, ${displayStoneType}`;
    }

    const metalDetailLi = document.querySelector('[data-detail-metal]');
    if (metalDetailLi) {
      let purityText = '';
      if (isSilver) {
        purityText = 'Sterling Silver 925';
      } else {
        purityText = '9K';
        if (data.title.includes('18k') || data.title.includes('18kt')) purityText = '18K';
        else if (data.title.includes('14k') || data.title.includes('14kt')) purityText = '14K';
        else if (data.title.includes('22k') || data.title.includes('22kt')) purityText = '22K';
        
        let colorText = 'Yellow Gold';
        if (data.title.toLowerCase().includes('rose')) colorText = 'Rose Gold';
        else if (data.title.toLowerCase().includes('rhodium') || data.title.toLowerCase().includes('white')) colorText = 'Rhodium Gold';
        
        purityText = `${purityText} ${colorText}`;
      }
      metalDetailLi.innerHTML = `<strong>Metal:</strong> ${purityText}`;
    }

    const skuDetailLi = document.querySelector('[data-detail-sku]');
    if (skuDetailLi) {
      skuDetailLi.innerHTML = `<strong>SKU:</strong> ${data.sku || ''}`;
    }

    // Dispatch svadezi:price-updated custom event
    const save_val = compare_at - price;
    const savePct_val = Math.round((save_val * 100) / compare_at);
    const emi_val = Math.round(price / 12);

    window.dispatchEvent(new CustomEvent('svadezi:price-updated', {
      detail: {
        current: price,
        compareAt: compare_at,
        save: save_val,
        savePct: savePct_val,
        emi: emi_val
      }
    }));
  }

  // Hook into variant change
  window.addEventListener('kt.product.variant.change', (event) => {
    if (event.detail && event.detail.id) {
      updateLivePrice(event.detail.id);
    }
  });

  // Expose global window.SvadeziPrice
  window.SvadeziPrice = {
    refresh: function() {
      const currentVariantId = document.querySelector('product-form input[name="id"]')?.value || document.querySelector('input[name="id"]')?.value;
      if (currentVariantId) {
        updateLivePrice(currentVariantId);
      }
    },
    compute: function(variantId) {
      updateLivePrice(variantId);
    }
  };

  // BUG 5 FIX: Expose WEIGHT_REGISTRY for cross-sell pricing in svadezi-pair-it-with.liquid
  window.__SvadeziWeightRegistry__ = WEIGHT_REGISTRY;

  // Also initial run on page load
  const initialVariantInput = document.querySelector('input[name="id"]');
  if (initialVariantInput) {
    updateLivePrice(initialVariantInput.value);
  }
  
  if (typeof window.initSvadeziCollectionPricing === 'function') {
    window.initSvadeziCollectionPricing();
  }
});


// SVADEZI Dynamic Metal Spec Tile logic
window.addEventListener("kt.product.variant.change", (e) => {
  const variant = e.detail && e.detail.selectedVariant;
  if (!variant) return;
  const metalSpecEl = document.querySelector('[data-spec-metal]');
  if (metalSpecEl) {
    let metalString = variant.option1 || '';
    let colorString = variant.option2 && variant.option2 !== 'Default Title' ? variant.option2 : '';

    // Clean up metal string
    if (metalString.toLowerCase().includes('silver')) {
      metalString = '925 Silver';
    } else if (metalString.toLowerCase().includes('gold')) {
      metalString = metalString.replace(/gold/i, '').trim();
    }

    // Clean up color string
    if (colorString) {
      colorString = colorString.replace(/gold/i, '').trim();
      metalString += ' &middot; ' + colorString;
    }

    metalSpecEl.innerHTML = metalString;
    metalSpecEl.style.fontSize = '12px';
    metalSpecEl.style.lineHeight = '1.3';
  }
});


// BUG 7 FIX: Removed stale kt.product.variant.change listener that was overwriting the sticky bar
// with Shopify's raw variant.price (₹0 for live-priced products). The mobile sticky bar is now
// updated exclusively via the svadezi:price-updated event dispatched by updateLivePrice().

// BUG FIX: Collection page live pricing 
window.initSvadeziCollectionPricing = function() {
  const cards = document.querySelectorAll('.pc-price[data-card-live-price]');
  if (!cards || !cards.length) return;
  
  let liveRates = null;
  try {
    liveRates = JSON.parse(sessionStorage.getItem('SvadeziLiveMetalRates'));
  } catch(e) {}
  
  if (!liveRates) return;
  
  const registry = window.__SvadeziWeightRegistry__;
  if (!registry) return;
  
  const formatter = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0, maximumFractionDigits: 0 });
  
  cards.forEach(card => {
    const sku = card.getAttribute('data-sku');
    const title = card.getAttribute('data-title') || '';
    if (!sku) return;
    
    const baseSku = sku.split('-').slice(0, 2).join('-');
    const data = registry[sku] || registry[baseSku];
    if (!data) return;
    
    const isSilver = title.toLowerCase().includes('silver') || title.includes('925');
    let grams = 0;
    if (isSilver) grams = data.Silver || 0;
    else grams = data['9K'] || 0;
    const carats = data.DiamondCt || 0;
    
    let liveRate = 0;
    let makingPerGram = 0;
    if (isSilver) {
      liveRate = liveRates.silver_999 || (liveRates.gold_24k / 70);
      makingPerGram = 550;
    } else {
      liveRate = liveRates.gold_9k || (liveRates.gold_24k * 0.375);
      makingPerGram = 850;
    }
    
    const metal_cost = grams * liveRate;
    const making = grams * makingPerGram;
    
    const diamond_rate = 35000;
    const diamond_cost = carats * diamond_rate;
    
    if (title.toLowerCase().includes('diamond') && carats === 0) {
      const curEl = card.querySelector('.pc-price__current');
      if (curEl) curEl.innerHTML = 'Price on Request';
      const compEl = card.querySelector('.pc-price__compare');
      if (compEl) compEl.style.display = 'none';
      return;
    }
    
    const subtotal = metal_cost + making + 800 + diamond_cost;
    const finalPrice = Math.round((subtotal * 1.03) / 10) * 10;
    const comparePrice = Math.round((finalPrice * 1.5) / 10) * 10;
    
    const curEl = card.querySelector('.pc-price__current');
    if (curEl) curEl.innerHTML = formatter.format(finalPrice);
    
    const compEl = card.querySelector('.pc-price__compare');
    if (compEl) {
      compEl.innerHTML = formatter.format(comparePrice);
      compEl.style.display = 'inline-block';
    }
  });
};

document.addEventListener('DOMContentLoaded', () => {
  if (typeof window.initSvadeziCollectionPricing === 'function') {
    window.initSvadeziCollectionPricing();
    setTimeout(window.initSvadeziCollectionPricing, 1500);
  }
});
